import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Container, Row, Col, Card, CardBody, Button, Spinner, Alert, Badge, Modal
} from "react-bootstrap";
import { Box } from "@mui/material";
import { 
  CheckCircle, XCircle, Clock, Warning, ArrowLeft, 
  Receipt as ReceiptIcon, ArrowsClockwise, Image, House 
} from "@phosphor-icons/react";
import html2canvas from "html2canvas";
import usePaymentStatus from "../../apis/PaymentStatusApi";
import AppLayout from "../../layouts/AppLayout";
import CompanyLogo from "../../components/CompanyLogo";

const PaymentReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const state = location.state || {};
  const orderId = state.orderId || state.order_id;
  const initialPaymentData = state.paymentData;

  const { getPaymentStatus, paymentData, isLoading, error } = usePaymentStatus();
  
  const [receiptData, setReceiptData] = useState(initialPaymentData || null);
  const [isExporting, setIsExporting] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);

  // ✅ REF for receipt content
  const receiptRef = useRef(null);

  // ✅ BLOCK BACK BUTTON - SHOW MODAL
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    
    const handleBack = () => {
      window.history.pushState(null, null, window.location.href);
      setShowBackModal(true);
    };

    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, []);

  // ✅ HANDLE MODAL ACTIONS
  const handleGoToDashboard = () => {
    setShowBackModal(false);
    navigate('/dashboard', { replace: true });
  };

  const handleStay = () => {
    setShowBackModal(false);
  };

  // ✅ AUTO-FETCH STATUS ON PAGE LOAD
  useEffect(() => {
    if (!orderId) {
      return;
    }

    getPaymentStatus(orderId, (resp) => {
      if (resp?.is_success && resp.data) {
        setReceiptData(resp.data);
      }
    });
  }, [orderId]);

  // ✅ UPDATE RECEIPT DATA FROM REDUX
  useEffect(() => {
    if (paymentData) {
      setReceiptData(paymentData);
    }
  }, [paymentData]);

  // ✅ MANUAL REFRESH HANDLER
  const handleRefresh = () => {
    if (!orderId) return;

    getPaymentStatus(orderId, (resp) => {
      if (resp?.is_success && resp.data) {
        setReceiptData(resp.data);
      }
    });
  };

  // ✅ EXPORT RECEIPT AS JPG
  const handleExportJPG = async () => {
    if (!receiptRef.current) {
      return;
    }

    setIsExporting(true);

    try {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      canvas.toBlob((blob) => {
        if (!blob) {
          setIsExporting(false);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const fileName = `Payment_Receipt_${orderId}_${new Date().getTime()}.jpg`;
        
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(url);
        setIsExporting(false);
      }, "image/jpeg", 0.95);

    } catch (error) {
      setIsExporting(false);
      alert("Failed to export receipt. Please try again.");
    }
  };

  // [NO ORDER ID]
  if (!orderId) {
    return (
      <AppLayout>
        <Container className="py-5 text-center">
          <Alert variant="warning">
            <Warning size={24} className="me-2" />
            No payment information found
          </Alert>
          <Button onClick={() => navigate("/dashboard/")}>
            Go to Dashboard
          </Button>
        </Container>
      </AppLayout>
    );
  }

  // [LOADING STATE]
  if (isLoading && !receiptData) {
    return (
      <AppLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading payment details...</p>
        </Container>
      </AppLayout>
    );
  }

  // [ERROR STATE]
  if (error && !receiptData) {
    return (
      <AppLayout>
        <Container className="py-5 text-center">
          <Alert variant="danger">
            <Warning size={24} className="me-2" />
            {error}
          </Alert>
          <Button variant="primary" onClick={handleRefresh} className="me-2">
            <ArrowsClockwise size={20} className="me-2" />
            Retry
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate("/dashboard/")}>
            Go to Home
          </Button>
        </Container>
      </AppLayout>
    );
  }

  // ✅ EXTRACT RECEIPT DATA
  const rawStatus = receiptData?.payment_status || "unknown";
  const amount = receiptData?.amount || 0;
  const gateway = receiptData?.gateway || "N/A";
  const transactionId = receiptData?.transaction_id || receiptData?.txn_id || "N/A";
  const createdAt = receiptData?.created_at || receiptData?.payment_date || new Date().toISOString();
  const customerName = receiptData?.customer_name || receiptData?.name || "Customer";
  const customerEmail = receiptData?.customer_email || receiptData?.email || "";
  const customerPhone = receiptData?.customer_phone || receiptData?.phone || "";
const receipt_no=receiptData?.receipt_no || receiptData?.receipt_no || "";
  // ✅ NORMALIZE STATUS
  const normalizedStatus = rawStatus.toString().toLowerCase().trim();

  // ✅ DETERMINE STATUS TYPE
  let statusType = "unknown";
  
  if (normalizedStatus === "success" || normalizedStatus === "completed" || normalizedStatus === "paid") {
    statusType = "success";
  } else if (normalizedStatus === "pending" || normalizedStatus === "processing" || normalizedStatus === "initiated") {
    statusType = "pending";
  } else if (normalizedStatus === "failed" || normalizedStatus === "cancelled" || normalizedStatus === "canceled" || normalizedStatus === "error") {
    statusType = "failed";
  }

  // ✅ STATUS CONFIGURATION
  const statusConfig = {
    success: {
      variant: "success",
      bgColor: "#d4edda",
      borderColor: "#c3e6cb",
      icon: <CheckCircle size={80} weight="fill" className="text-success mb-3" />,
      title: "Payment Successful",
      message: "Your payment has been processed successfully"
    },
    pending: {
      variant: "warning",
      bgColor: "#fff3cd",
      borderColor: "#ffeaa7",
      icon: <Clock size={80} weight="duotone" className="text-warning mb-3" />,
      title: "Payment Pending",
      message: "Your payment is being processed"
    },
    failed: {
      variant: "danger",
      bgColor: "#f8d7da",
      borderColor: "#f5c6cb",
      icon: <XCircle size={80} weight="fill" className="text-danger mb-3" />,
      title: "Payment Failed",
      message: "Your payment could not be processed"
    },
    unknown: {
      variant: "secondary",
      bgColor: "#e2e3e5",
      borderColor: "#d6d8db",
      icon: <Warning size={80} weight="duotone" className="text-secondary mb-3" />,
      title: "Payment Status Unknown",
      message: "Unable to determine payment status"
    }
  };

  const currentStatus = statusConfig[statusType];

  return (
    <AppLayout>
      <Container className="py-2 pb-6 mb-6">
        <Row className="justify-content-center pb-5 mb-5" >
          <Col xs={12} md={8} lg={6}>
            
            {/* ✅ RECEIPT CONTENT (REF FOR EXPORT) */}
            <div ref={receiptRef}>
              {/* HEADER CARD */}
              <Card 
                className="shadow-lg border-0 text-center mb-2"
                style={{
                  backgroundColor: currentStatus.bgColor,
                  borderLeft: `5px solid ${currentStatus.borderColor}`
                }}
              >
                <CardBody className="p-4">
                  {/* STATUS ICON */}
                  <div className="mb-2">
                    {currentStatus.icon}
                  </div>

                  {/* TITLE */}
                  <h3 className="mb-2">{currentStatus.title}</h3>
                  <p className="text-muted mb-4">{currentStatus.message}</p>

                  {/* AMOUNT */}
                  <div className="mb-3">
                    <h1 className="display-4 fw-bold text-primary">
                      ₹{parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </h1>
                  </div>

                  {/* STATUS BADGE */}
                  <Badge 
                    bg={currentStatus.variant} 
                    className="px-3 py-2 fs-6"
                  >
                    {rawStatus.toUpperCase()}
                  </Badge>

                  {/* LOADING INDICATOR */}
                  {isLoading && (
                    <div className="mt-3">
                      <Spinner size="sm" className="me-2" />
                      <small className="text-muted">Updating...</small>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* DETAILS CARD */}
              <Card className="shadow-sm border-0 mb-2">
                <CardBody className="p-4">
                  <h5 className="mb-3">
                    <ReceiptIcon size={20} className="me-2" weight="duotone" />
                    Payment Details
                  </h5>

                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted">Order ID : </td>
                        <td className="text-end fw-semibold">{orderId}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Transaction ID : </td>
                        <td className="text-end fw-semibold">{transactionId}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Payment Gateway : </td>
                        <td className="text-end fw-semibold text-uppercase">{gateway}</td>
                      </tr>
                       <tr>
                        <td className="text-muted">Receipt No : </td>
                        <td className="text-end fw-semibold">{receipt_no}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Date & Time : </td>
                        <td className="text-end fw-semibold">
                          {new Date(createdAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                      {customerName && customerName !== "Customer" && (
                        <tr>
                          <td className="text-muted">Customer Name</td>
                          <td className="text-end fw-semibold">{customerName}</td>
                        </tr>
                      )}
                      {customerEmail && (
                        <tr>
                          <td className="text-muted">Email</td>
                          <td className="text-end fw-semibold">{customerEmail}</td>
                        </tr>
                      )}
                      {customerPhone && (
                        <tr>
                          <td className="text-muted">Phone</td>
                          <td className="text-end fw-semibold">{customerPhone}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Company Logo */}
                  <Box textAlign="center" mt={3}>
                    <CompanyLogo width="120px" />
                  </Box>
                </CardBody>
              </Card>
            </div>

            {/* ACTION BUTTONS */}
            <div className="d-grid gap-2">
              {/* REFRESH BUTTON */}
              <Button 
                variant="outline-primary" 
                size="lg"
                onClick={handleRefresh}
                disabled={isLoading || isExporting}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={20} className="me-2" weight="bold" />
                    Refresh Status
                  </>
                )}
              </Button>

              {/* EXPORT AS JPG BUTTON */}
              <Button 
                variant="success" 
                size="lg"
                onClick={handleExportJPG}
                disabled={isLoading || isExporting}
              >
                {isExporting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Image size={20} className="me-2" weight="bold" />
                    Share
                  </>
                )}
              </Button>

              {/* BACK TO DASHBOARD */}
              <Button 
                variant="outline-secondary"
                onClick={() => navigate("/dashboard/", { replace: true })}
                disabled={isExporting}
              >
                <ArrowLeft size={20} className="me-2" />
                Back to Home
              </Button>
            </div>

            {/* INFO */}
            <div className="text-center mt-4 text-muted small">
              <p className="mb-4">
                💾 Save this receipt for your records
              </p>
            </div>

          </Col>
        </Row>
      </Container>

      {/* ✅ BACK BUTTON WARNING MODAL */}
      <Modal show={showBackModal} onHide={handleStay} centered backdrop="static">
        <Modal.Body className="text-center p-4">
          <Warning size={48} className="text-warning mb-3" />
          <h5 className="mb-3">Cannot Go Back!</h5>
          <p className="text-muted mb-4">
            Payment receipt is displayed. Going back is not recommended.
            <br />
            Please use the buttons below.
          </p>
          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              size="lg"
              onClick={handleGoToDashboard}
            >
              <House size={20} className="me-2" />
              Home
            </Button>
            <Button 
              variant="outline-secondary"
              onClick={handleStay}
            >
              Stay
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </AppLayout>
  );
};

export default PaymentReceipt;
